import type { ProColumns } from "@ant-design/pro-components";
import { Tag, Typography } from "antd";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { ADMIN_LIST_PAGINATION_STANDARD, readAdminStandardPageSize } from "./admin-table";
import { CatalogPickerModal } from "./catalog-picker-modal";
import { buildLocalizedTextHeaderFilter } from "./localized-text-header-filter";

export type EnvironmentSpecPickerRuntimeType = "vm" | "container" | "microvm" | "dynamips";

export type EnvironmentSpecPickerRecordBase = {
  id: string | number;
  code: string;
  title: string;
  description: string | null;
  runtime_type: EnvironmentSpecPickerRuntimeType;
  cpu?: number;
  memory_mb: number;
  ephemeral_storage_gb?: number | null;
  status: string;
};

export type EnvironmentSpecPickerFilterValues = {
  keyword?: string;
  code?: string;
  title?: string;
  description?: string;
  status?: string;
};

export type EnvironmentSpecPickerRequestParams = {
  runtimeType: EnvironmentSpecPickerRuntimeType;
  keyword?: string;
  filters: EnvironmentSpecPickerFilterValues;
  page: number;
  pageSize: number;
};

export type EnvironmentSpecPickerRequestResult<T extends EnvironmentSpecPickerRecordBase> = {
  items: T[];
  total: number;
};

export type EnvironmentSpecPickerLabels = {
  emptyText: ReactNode;
  columnTitles: {
    code: ReactNode;
    title: ReactNode;
    cpu: ReactNode;
    memory: ReactNode;
    ephemeralStorage: ReactNode;
    status: ReactNode;
    description: ReactNode;
  };
  filterPlaceholders: {
    code: string;
    title: string;
    description: string;
  };
  statusValueEnum: Record<string, { text: ReactNode }>;
  getStatusLabel: (status: string) => ReactNode;
  getStatusColor?: (status: string) => string | undefined;
  filterActions: { search: string; reset: string };
  messages?: {
    selectFirst: string;
  };
};

export type EnvironmentSpecPickerModalProps<T extends EnvironmentSpecPickerRecordBase> = {
  open: boolean;
  runtimeType: EnvironmentSpecPickerRuntimeType;
  title: ReactNode;
  submitText: string;
  cancelText: string;
  persistenceKey: string;
  labels: EnvironmentSpecPickerLabels;
  request: (
    params: EnvironmentSpecPickerRequestParams,
  ) => Promise<EnvironmentSpecPickerRequestResult<T>>;
  selectedRowKey: string | number | null;
  selectedRecord: T | null;
  onSelectionChange: (record: T | null, key: string | number | null) => void;
  onSubmit: (selected: T) => Promise<boolean> | boolean;
  onClose: () => void;
  onError?: (message: string) => void;
  onWarning?: (message: string) => void;
  width?: number | string;
  tableLayout?: "auto" | "fixed";
};

type ControlledFilters = Record<string, Array<string | number | boolean> | null | undefined>;

function readFilterValue(filters: ControlledFilters, key: string) {
  const value = filters[key]?.[0];
  if (value === null || value === undefined) return undefined;
  const normalized = String(value).trim();
  return normalized || undefined;
}

function readFilterValues(filters: ControlledFilters): EnvironmentSpecPickerFilterValues {
  const values: EnvironmentSpecPickerFilterValues = {};
  for (const key of ["keyword", "code", "title", "description", "status"] as const) {
    const value = readFilterValue(filters, key);
    if (value !== undefined) values[key] = value;
  }
  return values;
}

function renderText(value: string) {
  return (
    <Typography.Text ellipsis={{ tooltip: value }} style={{ display: "block", maxWidth: "100%" }}>
      {value}
    </Typography.Text>
  );
}

export function EnvironmentSpecPickerModal<T extends EnvironmentSpecPickerRecordBase>({
  open,
  runtimeType,
  title,
  submitText,
  cancelText,
  persistenceKey,
  labels,
  request,
  selectedRowKey,
  selectedRecord,
  onSelectionChange,
  onSubmit,
  onClose,
  onError,
  onWarning,
  width,
  tableLayout = "auto",
}: EnvironmentSpecPickerModalProps<T>) {
  const [filterValues, setFilterValues] = useState<EnvironmentSpecPickerFilterValues>({});
  const onSelectionChangeRef = useRef(onSelectionChange);

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  useEffect(() => {
    if (!open) return;
    setFilterValues({});
    onSelectionChangeRef.current(null, null);
  }, [open]);

  const columns = useMemo<ProColumns<T>[]>(
    () => [
      {
        title: labels.columnTitles.code,
        dataIndex: "code",
        ellipsis: true,
        render: (_, record) => renderText(record.code),
        ...buildLocalizedTextHeaderFilter(labels.filterPlaceholders.code, {
          search: labels.filterActions.search,
          reset: labels.filterActions.reset,
        }),
        filteredValue: filterValues.code ? [filterValues.code] : null,
      },
      {
        title: labels.columnTitles.title,
        dataIndex: "title",
        ellipsis: true,
        render: (_, record) => renderText(record.title),
        ...buildLocalizedTextHeaderFilter(labels.filterPlaceholders.title, {
          search: labels.filterActions.search,
          reset: labels.filterActions.reset,
        }),
        filteredValue: filterValues.title ? [filterValues.title] : null,
      },
      ...(runtimeType === "dynamips"
        ? []
        : [
            {
              title: labels.columnTitles.cpu,
              dataIndex: "cpu",
            } satisfies ProColumns<T>,
          ]),
      {
        title: labels.columnTitles.memory,
        dataIndex: "memory_mb",
      },
      ...(runtimeType === "dynamips"
        ? []
        : [
            {
              title: labels.columnTitles.ephemeralStorage,
              dataIndex: "ephemeral_storage_gb",
            } satisfies ProColumns<T>,
          ]),
      {
        title: labels.columnTitles.status,
        dataIndex: "status",
        valueType: "select",
        filters: true,
        valueEnum: labels.statusValueEnum,
        filteredValue: filterValues.status ? [filterValues.status] : null,
        render: (_, record) => (
          <Tag
            {...(() => {
              const color = labels.getStatusColor?.(record.status);
              return color === undefined ? {} : { color };
            })()}
          >
            {labels.getStatusLabel(record.status)}
          </Tag>
        ),
      },
      {
        title: labels.columnTitles.description,
        dataIndex: "description",
        ellipsis: true,
        render: (_, record) =>
          record.description === null ? null : renderText(record.description),
        ...buildLocalizedTextHeaderFilter(labels.filterPlaceholders.description, {
          search: labels.filterActions.search,
          reset: labels.filterActions.reset,
        }),
        filteredValue: filterValues.description ? [filterValues.description] : null,
      },
    ],
    [filterValues, labels, runtimeType],
  );

  return (
    <CatalogPickerModal<T>
      title={title}
      submitText={submitText}
      cancelText={cancelText}
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      columns={columns}
      persistenceKey={persistenceKey}
      onSubmit={async () => {
        if (!selectedRecord) {
          if (labels.messages?.selectFirst) {
            onWarning?.(labels.messages.selectFirst);
          }
          return false;
        }
        return onSubmit(selectedRecord);
      }}
      rowKey="id"
      selectedRowKeys={selectedRowKey === null ? [] : [selectedRowKey]}
      onSelectionChange={(keys, rows) => {
        onSelectionChange(rows[0] ?? null, keys[0] ?? null);
      }}
      emptyText={labels.emptyText}
      {...(width === undefined ? {} : { width })}
      rowSelectionType="radio"
      tableLayout={tableLayout}
      tableProps={{
        pagination: ADMIN_LIST_PAGINATION_STANDARD,
        onChange: (_pagination, filters) => {
          setFilterValues(readFilterValues(filters as ControlledFilters));
        },
        request: async (tableParams, _sorter, filters) => {
          const nextFilterValues = readFilterValues(filters as ControlledFilters);
          setFilterValues(nextFilterValues);
          try {
            const tableKeyword =
              typeof tableParams.keyword === "string"
                ? tableParams.keyword.trim() || undefined
                : undefined;
            const keyword = tableKeyword ?? nextFilterValues.keyword;
            const response = await request({
              runtimeType,
              ...(keyword === undefined ? {} : { keyword }),
              filters: {
                ...nextFilterValues,
                ...(keyword === undefined ? {} : { keyword }),
              },
              page: Math.max(1, Number(tableParams.current) || 1),
              pageSize: readAdminStandardPageSize(tableParams.pageSize),
            });
            return {
              data: response.items,
              total: response.total,
              success: true,
            };
          } catch (error) {
            onError?.(error instanceof Error ? error.message : String(error));
            return { data: [], total: 0, success: false };
          }
        },
      }}
    />
  );
}
